import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CpuChipIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

const MindsetAssessmentForm = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    growthMindset: data.growthMindset || {},
    entrepreneurialAttitudes: data.entrepreneurialAttitudes || {},
    problemSolving: data.problemSolving || {},
    motivation: data.motivation || {},
    selfEfficacy: data.selfEfficacy || {},
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const growthMindsetQuestions = [
    {
      id: 'learningOrientation',
      question: 'I believe my abilities can be developed through hard work and dedication',
      category: 'growthMindset',
    },
    {
      id: 'challengeSeeking',
      question: 'I actively seek out challenges that help me grow',
      category: 'growthMindset',
    },
    {
      id: 'feedbackReception',
      question: 'I welcome constructive feedback as an opportunity to improve',
      category: 'growthMindset',
    },
    {
      id: 'failureReframe',
      question: 'I view failures as learning opportunities rather than setbacks',
      category: 'growthMindset',
    },
    {
      id: 'effortValue',
      question: 'I believe that effort is the path to mastery',
      category: 'growthMindset',
    },
  ];

  const entrepreneurialAttitudesQuestions = [
    {
      id: 'opportunityRecognition',
      question: 'I naturally spot business opportunities in everyday situations',
      category: 'entrepreneurialAttitudes',
    },
    {
      id: 'innovationOrientation',
      question: 'I enjoy creating new solutions to existing problems',
      category: 'entrepreneurialAttitudes',
    },
    {
      id: 'customerFocus',
      question: 'I prioritize understanding and meeting customer needs',
      category: 'entrepreneurialAttitudes',
    },
    {
      id: 'valueCreation',
      question: 'I focus on creating value for others rather than just profit',
      category: 'entrepreneurialAttitudes',
    },
    {
      id: 'marketOrientation',
      question: 'I stay attuned to market trends and changes',
      category: 'entrepreneurialAttitudes',
    },
  ];

  const problemSolvingQuestions = [
    {
      id: 'analyticalThinking',
      question: 'I break down complex problems into manageable parts',
      category: 'problemSolving',
    },
    {
      id: 'creativeSolutions',
      question: 'I generate creative and unconventional solutions',
      category: 'problemSolving',
    },
    {
      id: 'systematicApproach',
      question: 'I follow a systematic process to solve problems',
      category: 'problemSolving',
    },
    {
      id: 'collaborativeProblemSolving',
      question: 'I involve others in problem-solving to get diverse perspectives',
      category: 'problemSolving',
    },
    {
      id: 'rapidPrototyping',
      question: 'I prefer to test solutions quickly rather than perfect them first',
      category: 'problemSolving',
    },
  ];

  const motivationQuestions = [
    {
      id: 'intrinsicMotivation',
      question: 'I am motivated by the work itself rather than external rewards',
      category: 'motivation',
    },
    {
      id: 'achievementOrientation',
      question: 'I set high standards for myself and strive to exceed them',
      category: 'motivation',
    },
    {
      id: 'autonomySeeking',
      question: 'I prefer to work independently and make my own decisions',
      category: 'motivation',
    },
    {
      id: 'purposeDriven',
      question: 'I am driven by a sense of purpose and making a difference',
      category: 'motivation',
    },
    {
      id: 'persistence',
      question: 'I maintain high motivation even when facing obstacles',
      category: 'motivation',
    },
  ];

  const selfEfficacyQuestions = [
    {
      id: 'businessConfidence',
      question: 'I am confident in my ability to start and run a business',
      category: 'selfEfficacy',
    },
    {
      id: 'skillConfidence',
      question: 'I believe I can learn the skills needed for business success',
      category: 'selfEfficacy',
    },
    {
      id: 'networkConfidence',
      question: 'I am confident in my ability to build professional relationships',
      category: 'selfEfficacy',
    },
    {
      id: 'financialConfidence',
      question: 'I am confident in my ability to manage business finances',
      category: 'selfEfficacy',
    },
    {
      id: 'leadershipConfidence',
      question: 'I am confident in my ability to lead and inspire others',
      category: 'selfEfficacy',
    },
  ];

  const handleResponseChange = (category, questionId, response) => {
    setAssessmentData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [questionId]: response,
      },
    }));
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const categories = [
      'growthMindset',
      'entrepreneurialAttitudes',
      'problemSolving',
      'motivation',
      'selfEfficacy',
    ];

    categories.forEach(category => {
      const categoryData = assessmentData[category];
      Object.values(categoryData).forEach(response => {
        if (response === 'stronglyAgree') {
          totalPoints += 4;
        } else if (response === 'agree') {
          totalPoints += 3;
        } else if (response === 'neutral') {
          totalPoints += 2;
        } else if (response === 'disagree') {
          totalPoints += 1;
        }
        maxPoints += 4;
      });
    });

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 75) return 'Strong Entrepreneurial Mindset';
    if (score >= 60) return 'Developing Entrepreneurial Mindset';
    if (score >= 45) return 'Basic Entrepreneurial Mindset';
    return 'Needs Mindset Development';
  };

  const renderQuestionSection = (title, questions, category) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-6 text-xl font-semibold text-gray-900">{title}</h3>

      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b border-gray-100 pb-4 last:border-b-0">
            <p className="mb-3 text-sm font-medium text-gray-900">
              {index + 1}. {question.question}
            </p>

            <div className="flex flex-wrap gap-2">
              {[
                {
                  value: 'stronglyAgree',
                  label: 'Strongly Agree',
                  icon: CheckCircleIcon,
                  color: 'green',
                },
                { value: 'agree', label: 'Agree', icon: CheckCircleIcon, color: 'blue' },
                { value: 'neutral', label: 'Neutral', icon: MinusCircleIcon, color: 'gray' },
                { value: 'disagree', label: 'Disagree', icon: XCircleIcon, color: 'red' },
              ].map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => handleResponseChange(category, question.id, value)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    assessmentData[category][question.id] === value
                      ? `bg-${color}-100 border-${color}-500 text-${color}-700`
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-green-200 bg-green-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <CpuChipIcon className="h-8 w-8 text-green-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Entrepreneurial Mindset Assessment</h2>
            <p className="text-gray-600">
              Evaluate your mindset, attitudes, and cognitive patterns related to entrepreneurship
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-green-600 px-6 py-3 font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      {renderQuestionSection('Growth Mindset', growthMindsetQuestions, 'growthMindset')}

      {renderQuestionSection(
        'Entrepreneurial Attitudes',
        entrepreneurialAttitudesQuestions,
        'entrepreneurialAttitudes'
      )}

      {renderQuestionSection('Problem-Solving Approach', problemSolvingQuestions, 'problemSolving')}

      {renderQuestionSection('Motivation & Drive', motivationQuestions, 'motivation')}

      {renderQuestionSection('Self-Efficacy & Confidence', selfEfficacyQuestions, 'selfEfficacy')}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
          placeholder="Add any additional observations about your mindset, areas of strength, or development needs..."
        />
      </motion.div>
    </div>
  );
};

export default MindsetAssessmentForm;
